import { useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import SidebarNew, { type SidebarSession } from "./SidebarNew";
import TopbarNew from "./TopbarNew";
import IntroTour from "@/components/IntroTour/IntroTour";
import { IconSidebarCollapse } from "@/components/Icons";
import {
  PageHeaderProvider,
  type PageHeader,
} from "./PageHeaderContext";

interface AppShellNewProps {
  children: ReactNode;
  workflowCount?: number;
  reportCount?: number;
  watchdogCount?: number;
  onNewChat?: () => void;
  sessions?: SidebarSession[];
  chatsLoaded?: boolean;
  activeChatId?: string;
  onSwitchChat?: (id: string) => void;
  onDeleteChat?: (id: string) => void;
  onRenameChat?: (id: string, title: string) => void;
}

const PATH_TITLES: Array<[RegExp, PageHeader]> = [
  [/^\/app\/audit/, { title: "Audit", subtitle: "Everything Foreman has done" }],
  [/^\/app\/chat/, { title: "Chat" }],
  [/^\/app\/actions/, { title: "Actions" }],
  [/^\/app\/workflows/, { title: "Actions" }],
  [/^\/app\/reports/, { title: "Reports" }],
  [/^\/app\/lookouts/, { title: "Lookouts" }],
  [/^\/app\/watchdogs/, { title: "Lookouts" }],
  [/^\/app\/settings/, { title: "Settings" }],
  [/^\/app\/email/, { title: "Email" }],
  [/^\/app\/whatsapp/, { title: "WhatsApp" }],
  [/^\/app\/crm/, { title: "CRM" }],
  [/^\/app\/gateway/, { title: "Gateway" }],
  [/^\/app\/memory/, { title: "Memory" }],
  [/^\/app\/profile/, { title: "Profile" }],
  [/^\/app\/organisation/, { title: "Organisation" }],
];

function defaultHeaderFor(pathname: string): PageHeader {
  for (const [re, header] of PATH_TITLES) {
    if (re.test(pathname)) return header;
  }
  return { title: "Foreman" };
}

// Topbar hidden across all app pages — each page sets its own header.
const HIDE_TOPBAR_PATHS = [/^\/app\//, /^\/app$/];

export default function AppShellNew({
  children,
  workflowCount,
  reportCount,
  watchdogCount,
  onNewChat,
  sessions,
  chatsLoaded,
  activeChatId,
  onSwitchChat,
  onDeleteChat,
  onRenameChat,
}: AppShellNewProps) {
  const location = useLocation();
  const initialHeader = defaultHeaderFor(location.pathname);
  const hideTopbar = HIDE_TOPBAR_PATHS.some((re) => re.test(location.pathname));
  const [collapsed, setCollapsed] = useState(false);
  // Off-canvas sidebar on mobile — hidden by default, slides in when
  // the hamburger is tapped, closes on backdrop tap or nav click.
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    document.body.classList.add("app-body");
    return () => {
      document.body.classList.remove("app-body");
    };
  }, []);

  // Close the mobile drawer on every route change.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Allow other components (intro tour) to drive the drawer.
  // Detail.open === true opens it, false closes it.
  useEffect(() => {
    const onSet = (e: Event) => {
      const detail = (e as CustomEvent<{ open: boolean }>).detail;
      if (typeof detail?.open === "boolean") {
        setMobileOpen(detail.open);
      }
    };
    window.addEventListener("foreman:sidebar:mobile-set", onSet);
    return () => window.removeEventListener("foreman:sidebar:mobile-set", onSet);
  }, []);

  // Lock body scroll while the drawer is open.
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  return (
    <PageHeaderProvider defaultHeader={initialHeader}>
      <div className="app-shell">
        <SidebarNew
          workflowCount={workflowCount}
          reportCount={reportCount}
          watchdogCount={watchdogCount}
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed((v) => !v)}
          onNewChat={onNewChat}
          sessions={sessions}
          chatsLoaded={chatsLoaded}
          activeChatId={activeChatId}
          onSwitchChat={onSwitchChat}
          onDeleteChat={onDeleteChat}
          onRenameChat={onRenameChat}
          mobileOpen={mobileOpen}
          onMobileClose={() => setMobileOpen(false)}
        />
        {/* Mobile backdrop — only renders when drawer is open. Tap closes. */}
        {mobileOpen && (
          <div
            className="as-mobile-backdrop"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
        )}
        <main className="as-main">
          {/* Mobile-only floating sidebar open button.
              No header strip, no border, no logo — just the same
              collapse icon the desktop sidebar uses, on a transparent
              tap target floated top-left. Hidden ≥881px. */}
          <button
            type="button"
            className="as-mobile-open-btn"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <IconSidebarCollapse width={18} height={18} />
          </button>
          {!hideTopbar && <TopbarNew />}
          <div
            className="as-scroll"
            style={
              hideTopbar
                ? { display: "flex", flexDirection: "column" }
                : undefined
            }
          >
            {children}
          </div>
        </main>
      </div>
      <IntroTour />
    </PageHeaderProvider>
  );
}
